import { useSyncExternalStore } from 'react';
import type { ChatMessage } from '../../utils/dxfApi';

/**
 * Assistant-chat session (messages per floor plan, open/busy flags) kept OUTSIDE the widget.
 *
 * The widget unmounts every time the 3D view opens (EditorLayout only renders it with the 2D
 * chrome), so component state lost the whole conversation on a 2D -> 3D -> 2D switch. A route
 * generation also keeps posting progress for up to a minute — messages that arrive while the widget
 * is hidden land here and show up when it remounts. Only a full page reload resets the session.
 */
const GREETING: ChatMessage = { role: 'assistant', content: 'Hi! How can I help you?' };

type Session = { messages: Record<string, ChatMessage[]>; open: boolean; busy: boolean };

let session: Session = { messages: {}, open: false, busy: false };
const listeners = new Set<() => void>();

const keyFor = (floorPlanId: string | null | undefined) => floorPlanId ?? 'local';
const NO_MESSAGES: ChatMessage[] = [GREETING];

function update(next: Partial<Session>): void {
  session = { ...session, ...next };       // new object => subscribers see the change
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Append a message to a floor plan's conversation (works while the widget is unmounted). */
export function appendChatMessage(floorPlanId: string | null | undefined, message: ChatMessage): void {
  const key = keyFor(floorPlanId);
  const current = session.messages[key] ?? NO_MESSAGES;
  update({ messages: { ...session.messages, [key]: [...current, message] } });
}

export const setChatOpen = (open: boolean) => update({ open });
export const setChatBusy = (busy: boolean) => update({ busy });

/** The conversation for this floor plan plus the shared open/busy flags. */
export function useChatSession(floorPlanId: string | null | undefined) {
  const snap = useSyncExternalStore(subscribe, () => session);
  return {
    messages: snap.messages[keyFor(floorPlanId)] ?? NO_MESSAGES,
    open: snap.open,
    busy: snap.busy,
  };
}
